import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStringsContext } from '../../../../contexts/strings';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import PollAlternativeItem, { PollAlternativeItemProps } from './poll-alternative-item';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import { QuizAlternative, QuizQuestion } from '../types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

const getRandomId = () => Math.floor(Math.random() * (100000 - 10000) + 10000);

const defaultQuizAlternative: QuizAlternative = {
  id: -1,
  text: '',
};

interface SortableItemProps extends PollAlternativeItemProps {
  id: number;
}

const SortableItem = ({ id, ...props }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box sx={style} ref={setNodeRef} {...attributes}>
      <PollAlternativeItem isActive={isDragging} listeners={listeners} {...props} />
    </Box>
  );
};

type AlternativesTextKey = keyof Pick<QuizAlternative, 'text'>;
type QuestionTextAlternativesKey = keyof Pick<QuizQuestion, 'textAlternatives'>;
type QuestionAnswerKey = keyof Pick<QuizQuestion, 'answer'>;

const textKey: AlternativesTextKey = 'text';
const textAlternativesKey: QuestionTextAlternativesKey = 'textAlternatives';
const answerKey: QuestionAnswerKey = 'answer';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  onValue: (key: QuestionTextAlternativesKey | QuestionAnswerKey, value: any | null) => void;
  selectedQuestion: QuizQuestion;
  isEditing: boolean;
}

const PollQuizTextAlternatives = ({
  isEditing,
  selectedQuestion,
  onValue,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const sensors = useSensors(useSensor(PointerSensor));
  const initAlternativesValues = useRef(false);

  const [quizAlternatives, setQuizAlternatives] = useState<QuizAlternative[]>(
    isEditing && selectedQuestion.type === 'text' && selectedQuestion.textAlternatives
      ? [...selectedQuestion.textAlternatives]
      : [
          { ...defaultQuizAlternative, id: getRandomId() },
          { ...defaultQuizAlternative, id: getRandomId() },
        ]
  );
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(
    isEditing && selectedQuestion.answer ? selectedQuestion.answer : quizAlternatives[0].id
  );
  const [alternativeActive, setAlternativeActive] = useState<QuizAlternative | null>(null);

  useEffect(() => {
    if (!initAlternativesValues.current) {
      onValue(textAlternativesKey, quizAlternatives);
      onValue(answerKey, selectedAnswerId);
      initAlternativesValues.current = true;
    }
  }, [onValue, quizAlternatives, selectedAnswerId]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const alternative = quizAlternatives.find(({ id }) => id === active.id);
    setAlternativeActive(alternative!);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = quizAlternatives.findIndex(({ id }) => id === active.id);
      const newIndex = quizAlternatives.findIndex(({ id }) => id === over.id);
      const updatedAlternatives = arrayMove(quizAlternatives, oldIndex, newIndex);
      setQuizAlternatives(updatedAlternatives);
      onValue(textAlternativesKey, updatedAlternatives);
    }
    setAlternativeActive(null);
  };

  const handleAlternativesValue = useCallback(
    (
      key: AlternativesTextKey,
      value: string | null,
      alternative: QuizAlternative,
      alternativeIndex: number
    ) => {
      alternative[key] = value || '';
      const updatedAlternatives = quizAlternatives.map((a, index) =>
        index === alternativeIndex ? alternative : a
      );
      setQuizAlternatives(updatedAlternatives);
      onValue(textAlternativesKey, updatedAlternatives);
    },
    [onValue, quizAlternatives]
  );

  const onAddAlternative = useCallback(() => {
    const updatedAlternatives = [
      ...quizAlternatives,
      { ...defaultQuizAlternative, id: getRandomId() },
    ];
    setQuizAlternatives(updatedAlternatives);
    onValue(textAlternativesKey, updatedAlternatives);
  }, [onValue, quizAlternatives]);

  const onDelete = useCallback(
    (alternativeIndex: number) => {
      const removedAlternatives = quizAlternatives.splice(alternativeIndex, 1);

      const isAnswerRemoved = removedAlternatives.some(({ id }) => id === selectedAnswerId);
      if (isAnswerRemoved) {
        const firstAlternativeId = quizAlternatives[0].id;
        setSelectedAnswerId(firstAlternativeId);
        onValue(answerKey, firstAlternativeId);
      }

      setQuizAlternatives([...quizAlternatives]);
      onValue(textAlternativesKey, quizAlternatives);
    },
    [onValue, quizAlternatives, selectedAnswerId]
  );

  const onCheck = useCallback(
    (checked: boolean, alternative: QuizAlternative) => {
      setSelectedAnswerId(alternative.id);
      onValue(answerKey, alternative.id);
    },
    [onValue]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <Stack spacing={2}>
        <Typography variant="body2-bold">{strings.polls.quizAlternativeTextTitle}</Typography>
        <Stack spacing={4}>
          <SortableContext items={quizAlternatives} strategy={verticalListSortingStrategy}>
            {quizAlternatives.map((alternative, index) => (
              <SortableItem
                key={alternative.id}
                id={alternative.id}
                label={strings.polls.quizAlternativeLabel + String(index + 1)}
                name={textKey}
                value={alternative.text}
                onValue={(key, value) =>
                  handleAlternativesValue(textKey, value, alternative, index)
                }
                onDelete={() => onDelete(index)}
                disabled={quizAlternatives.length < 3}
                hideValidationError={hideValidationError}
                onCheck={(checked) => onCheck(checked, alternative)}
                checked={selectedAnswerId === alternative.id}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {alternativeActive ? (
              <PollAlternativeItem
                name={textKey}
                value={alternativeActive.text}
                label=""
                hideValidationError={true}
                checked={selectedAnswerId === alternativeActive.id}
                disabled
                isDragging
              />
            ) : null}
          </DragOverlay>
          <Button
            sx={{ maxWidth: '200px' }}
            size="small"
            variant="outlined"
            onClick={onAddAlternative}
            disabled={quizAlternatives.length >= 10}
          >
            {strings.polls.quizAddAlternative}
          </Button>
        </Stack>
      </Stack>
    </DndContext>
  );
};

export default PollQuizTextAlternatives;
